import {
  POSIcon,
  DashboardIcon,
  OrderIcon,
  TableIcon,
  MenuIcon,
  CustomerIcon,
  ReportIcon,
  PaymentIcon,
  StaffIcon,
  NotificationIcon,
  IntegrationIcon,
  SettingsIcon
} from '../components/layout/PosIcons';

import {
  ComputerDesktopIcon,
  PhoneIcon,
  UserIcon,
  QueueListIcon,
  ShoppingCartIcon,
  CubeIcon,
  MegaphoneIcon,
  BanknotesIcon,
  UsersIcon,
  Cog6ToothIcon,
  TableCellsIcon,
  PresentationChartBarIcon,
  ArrowTrendingUpIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  ClipboardDocumentListIcon,
  BriefcaseIcon,
  BoltIcon,
  ChartBarIcon,
  HomeIcon,
  SparklesIcon,
  RocketLaunchIcon,
  DocumentChartBarIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

import { getWorkingRoute, isWorkingRoute } from '../utils/routeMapping';
import { db } from './firebase';
import { doc, getDoc } from 'firebase/firestore';

// Sidebar configuration version to force cache invalidation on critical updates
const SIDEBAR_VERSION = '2026-04-19-v10';

// Icon mapping for dynamic menus
const ICON_MAP = {
  POSIcon,
  DashboardIcon,
  OrderIcon,
  TableIcon,
  MenuIcon,
  CustomerIcon,
  ReportIcon,
  PaymentIcon,
  StaffIcon,
  NotificationIcon,
  IntegrationIcon,
  SettingsIcon,
  ComputerDesktopIcon,
  PhoneIcon,
  UserIcon,
  QueueListIcon,
  ShoppingCartIcon,
  CubeIcon,
  MegaphoneIcon,
  BanknotesIcon,
  UsersIcon,
  Cog6ToothIcon,
  TableCellsIcon,
  PresentationChartBarIcon,
  ArrowTrendingUpIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  ClipboardDocumentListIcon,
  BriefcaseIcon,
  BoltIcon,
  ChartBarIcon,
  HomeIcon,
  SparklesIcon,
  RocketLaunchIcon,
  DocumentChartBarIcon,
  ChatBubbleLeftRightIcon
};

// Default business sidebar configuration
const DEFAULT_BUSINESS_SIDEBAR_CONFIG = [
  {
    id: 'dashboard',
    type: 'single',
    path: '/dashboard',
    icon: 'DashboardIcon',
    label: 'Anasayfa',
    color: 'from-blue-500 to-indigo-400'
  },
  {
    id: 'pos',
    type: 'group',
    label: 'POS & Satış',
    icon: 'POSIcon',
    color: 'from-green-500 to-emerald-400',
    children: [
      {
        path: '/sectors/restaurant/pos/table-service',
        icon: 'TableIcon',
        label: 'Masa Servisi'
      },
      {
        path: '/sectors/restaurant/pos/quick-sales',
        icon: 'OrderIcon',
        label: 'Hızlı Satış'
      },
      {
        path: '/sectors/restaurant/pos/kiosk',
        icon: 'ComputerDesktopIcon',
        label: 'Self-Service Kiosk'
      }
    ]
  },
  {
    id: 'finance',
    type: 'group',
    label: 'Finansal Yönetim',
    icon: 'PaymentIcon',
    color: 'from-blue-500 to-cyan-400',
    children: [
      {
        path: '/sectors/food-beverage/finance/income-analysis',
        icon: 'PaymentIcon',
        label: 'Gelir Analizi'
      },
      {
        path: '/sectors/food-beverage/finance/expense-management',
        icon: 'PaymentIcon',
        label: 'Gider Yönetimi'
      },
      {
        path: '/sectors/food-beverage/finance/cash-register-tracking',
        icon: 'PaymentIcon',
        label: 'Kasa Takibi'
      },
      {
        path: '/sectors/food-beverage/finance/tax-management',
        icon: 'PaymentIcon',
        label: 'Vergi Yönetimi'
      }
    ]
  },
  {
    id: 'kitchen',
    type: 'group',
    label: 'Mutfak Sistemi',
    icon: 'MenuIcon',
    color: 'from-red-500 to-pink-400',
    children: [
      {
        path: '/sectors/food-beverage/kitchen/kitchen-screen',
        icon: 'ComputerDesktopIcon',
        label: 'Mutfak Ekranı'
      },
      {
        path: '/sectors/food-beverage/operations/inventory-management',
        icon: 'MenuIcon',
        label: 'Stok Takibi'
      },
      {
        path: '/sectors/food-beverage/finance/suppliers',
        icon: 'UserIcon',
        label: 'Tedarikçiler'
      }
    ]
  },
  {
    id: 'reservations',
    type: 'group',
    label: 'Rezervasyon & Müşteri',
    icon: 'TableIcon',
    color: 'from-cyan-500 to-blue-400',
    children: [
      {
        path: '/sectors/food-beverage/reservations',
        icon: 'TableIcon',
        label: 'Masa Rezervasyonu'
      },
      {
        path: '/sectors/food-beverage/customers/customer-management',
        icon: 'CustomerIcon',
        label: 'Müşteri Yönetimi'
      }
    ]
  },
  {
    id: 'reports',
    type: 'single',
    path: '/sectors/restaurant/reports/dashboard',
    label: 'Raporlar & Analitik',
    icon: 'ReportIcon',
    color: 'from-violet-500 to-purple-400'
  },
  {
    id: 'social_media',
    type: 'group',
    label: 'Sosyal Medya & AI',
    icon: 'IntegrationIcon',
    color: 'from-pink-500 to-rose-400',
    children: [
      {
        path: '/social-media-post-form',
        icon: 'IntegrationIcon',
        label: 'AI Post Oluştur'
      },
      {
        path: '/ai-content-generator',
        icon: 'NotificationIcon',
        label: '✨ AI İçerik Üretici'
      },
      {
        path: '/content-calendar',
        icon: 'TableIcon',
        label: '📅 İçerik Takvimi'
      },
      {
        path: '/social-media-dashboard',
        icon: 'ReportIcon',
        label: '📊 Sosyal Medya Dashboard'
      }
    ]
  },
  {
    id: 'campaigns',
    type: 'single',
    path: '/integrations/campaigns',
    icon: 'NotificationIcon',
    label: 'Kampanyalar',
    color: 'from-yellow-500 to-amber-400'
  },
  {
    id: 'whatsapp',
    type: 'group',
    label: 'WhatsApp Modülü',
    icon: 'IntegrationIcon',
    color: 'from-green-500 to-emerald-400',
    children: [
      {
        path: '/business/whatsapp',
        icon: 'IntegrationIcon',
        label: 'Ana Sayfa'
      },
      {
        path: '/business/whatsapp/integration',
        icon: 'SettingsIcon',
        label: 'Entegrasyon Ayarları'
      },
      {
        path: '/business/whatsapp/conversations',
        icon: 'CustomerIcon',
        label: 'Görüşme Geçmişi'
      },
      {
        path: '/business/whatsapp/orders',
        icon: 'OrderIcon',
        label: 'Siparişler'
      },
      {
        path: '/business/whatsapp/reservations',
        icon: 'TableIcon',
        label: 'Rezervasyonlar'
      },
      {
        path: '/business/whatsapp/analytics',
        icon: 'ReportIcon',
        label: 'Raporlama'
      },
      {
        path: '/business/whatsapp/faq',
        icon: 'MenuIcon',
        label: 'Sık Sorulan Sorular'
      }
    ]
  },
  {
    id: 'settings',
    type: 'single',
    path: '/sectors/restaurant/settings',
    icon: 'SettingsIcon',
    label: 'Ayarlar',
    color: 'from-gray-500 to-slate-400'
  }
];

export class MenuConfigService {
  static getBusinessSidebarConfig() {
    try {
      // 🔄 Version Check: If stored version is different from current, clear cache
      const storedVersion = localStorage.getItem('sidebar_config_version');
      if (storedVersion !== SIDEBAR_VERSION) {
        console.log(`🔄 Sidebar version mismatch (${storedVersion} vs ${SIDEBAR_VERSION}). Clearing cache...`);
        this.clearAllSidebarCache();
        localStorage.setItem('sidebar_config_version', SIDEBAR_VERSION);
        // After clearing, we definitely want to use defaults or re-fetch from Firebase
      }

      // First try the direct businessSidebar key (from AdminSidebarManagerNew)
      const directConfig = localStorage.getItem('businessSidebarConfig');
      if (directConfig) {
        const businessConfig = JSON.parse(directConfig);
        console.log('✅ Loading business sidebar from localStorage (direct):', businessConfig);
        return this.validateMenuRoutes(businessConfig);
      }

      // Fallback to adminSidebarConfig (old format)
      const savedConfig = localStorage.getItem('adminSidebarConfig');
      if (savedConfig) {
        const config = JSON.parse(savedConfig);
        const businessConfig = config.businessSidebar || DEFAULT_BUSINESS_SIDEBAR_CONFIG;
        console.log('✅ Loading business sidebar from localStorage (fallback):', businessConfig);
        return this.validateMenuRoutes(businessConfig);
      }
    } catch (error) {
      console.error('Error loading menu config:', error);
    }

    console.log('⚠️ Using default business sidebar config');
    return this.validateMenuRoutes(DEFAULT_BUSINESS_SIDEBAR_CONFIG);
  }

  static async getBusinessSidebarConfigAsync() {
    try {
      // First try to load from Firebase sidebarConfigs/business
      if (db) {
        try {
          const businessConfigRef = doc(db, 'sidebarConfigs', 'business');
          const docSnap = await getDoc(businessConfigRef);

          if (docSnap.exists()) {
            const data = docSnap.data();
            // Support both new format (menuConfig) and old format (menuItems)
            const menuConfig = data.menuConfig || data.menuItems || DEFAULT_BUSINESS_SIDEBAR_CONFIG;
            console.log('🔍 Loading business sidebar from Firebase sidebarConfigs/business:', menuConfig?.length || 0, 'items');
            console.log('📊 DEBUG MenuConfigService Business: Data keys:', Object.keys(data));
            console.log('📅 DEBUG MenuConfigService Business: Last updated:', data.updated_at || data.lastUpdated);

            const businessConfig = menuConfig;
            const validatedConfig = this.validateMenuRoutes(businessConfig);

            // Save to localStorage for caching
            localStorage.setItem('businessSidebarConfig', JSON.stringify(businessConfig));

            console.log('✅ Business sidebar loaded from Firebase:', validatedConfig);
            return validatedConfig;
          } else {
            console.log('⚠️ No Firebase business config found at sidebarConfigs/business, using localStorage fallback');
          }
        } catch (firebaseError) {
          console.warn('⚠️ Firebase load failed for business sidebar, using localStorage fallback:', firebaseError);
        }
      }

      // Try direct localStorage key first (from AdminSidebarManagerNew)
      const directConfig = localStorage.getItem('businessSidebarConfig');
      if (directConfig) {
        const businessConfig = JSON.parse(directConfig);
        console.log('✅ Loading business sidebar from localStorage (direct async):', businessConfig);
        return this.validateMenuRoutes(businessConfig);
      }

      // Fallback to adminSidebarConfig structure
      const savedConfig = localStorage.getItem('adminSidebarConfig');
      if (savedConfig) {
        const config = JSON.parse(savedConfig);
        const businessConfig = config.businessSidebar || DEFAULT_BUSINESS_SIDEBAR_CONFIG;
        console.log('✅ Loading business sidebar from localStorage (fallback async):', businessConfig);
        return this.validateMenuRoutes(businessConfig);
      }
    } catch (error) {
      console.error('Error loading menu config:', error);
    }
    return this.validateMenuRoutes(DEFAULT_BUSINESS_SIDEBAR_CONFIG);
  }

  static getMainSidebarConfig() {
    try {
      const savedConfig = localStorage.getItem('adminSidebarConfig');
      if (savedConfig) {
        const config = JSON.parse(savedConfig);
        return config.mainSidebar || [];
      }
    } catch (error) {
      console.error('Error loading menu config:', error);
    }
    return [];
  }

  static getAdminSidebarConfig() {
    console.log('MenuConfigService.getAdminSidebarConfig: Called');
    try {
      // First try to get from localStorage (direct admin config)
      const directAdminConfig = localStorage.getItem('adminSidebarConfig');
      console.log('MenuConfigService.getAdminSidebarConfig: Direct admin config:', directAdminConfig);

      if (directAdminConfig) {
        const config = JSON.parse(directAdminConfig);
        console.log('MenuConfigService.getAdminSidebarConfig: Returning direct config:', config);
        return Array.isArray(config) ? config : [];
      }

      // Fallback to legacy format
      const savedConfig = localStorage.getItem('menuConfig');
      if (savedConfig) {
        const config = JSON.parse(savedConfig);
        console.log('MenuConfigService.getAdminSidebarConfig: Legacy config found:', config.adminSidebar);
        return config.adminSidebar || [];
      }
    } catch (error) {
      console.error('MenuConfigService.getAdminSidebarConfig: Error loading menu config:', error);
    }
    console.log('MenuConfigService.getAdminSidebarConfig: Returning empty array');
    return [];
  }

  // Add Firebase-based loading method
  static async loadAdminSidebarConfigFromFirebase() {
    try {
      const configDoc = doc(db, 'sidebarConfigs', 'admin');
      const docSnap = await getDoc(configDoc);

      if (docSnap.exists()) {
        const data = docSnap.data();
        // Support both new format (menuConfig) and old format (menuItems)
        const menuConfig = data.menuConfig || data.menuItems || [];
        console.log('📥 Admin sidebar config loaded from Firebase:', menuConfig);
        console.log('📊 DEBUG MenuConfigService Admin: Data keys:', Object.keys(data));
        console.log('📅 DEBUG MenuConfigService Admin: Last updated:', data.updated_at || data.lastUpdated);

        // Save to localStorage for caching
        localStorage.setItem('adminSidebarConfig', JSON.stringify(menuConfig));

        return menuConfig;
      } else {
        console.log('📝 No Firebase admin config found');
        return [];
      }
    } catch (error) {
      console.error('❌ Error loading admin config from Firebase:', error);
      return [];
    }
  }

  static getBusinessOwnerSidebarConfig() {
    try {
      // Check for direct business sidebar config
      const directBusinessConfig = localStorage.getItem('businessSidebarConfig');
      if (directBusinessConfig) {
        const config = JSON.parse(directBusinessConfig);
        return Array.isArray(config) ? config : [];
      }

      // Fallback to legacy format
      const savedConfig = localStorage.getItem('menuConfig');
      if (savedConfig) {
        const config = JSON.parse(savedConfig);
        return config.businessOwnerSidebar || [];
      }
    } catch (error) {
      console.error('Error loading business menu config:', error);
    }
    return [];
  }

  // Add Firebase-based loading method for business sidebar
  static async loadBusinessSidebarConfigFromFirebase() {
    try {
      const configDoc = doc(db, 'sidebarConfigs', 'business');
      const docSnap = await getDoc(configDoc);

      if (docSnap.exists()) {
        const data = docSnap.data();
        // Support both new format (menuConfig) and old format (menuItems)
        const menuConfig = data.menuConfig || data.menuItems || [];
        console.log('📥 Business sidebar config loaded from Firebase:', menuConfig);
        console.log('📊 DEBUG MenuConfigService Business Firebase: Data keys:', Object.keys(data));
        console.log('📅 DEBUG MenuConfigService Business Firebase: Last updated:', data.updated_at || data.lastUpdated);

        // Save to localStorage for caching
        localStorage.setItem('businessSidebarConfig', JSON.stringify(menuConfig));

        return menuConfig;
      } else {
        console.log('📝 No Firebase business config found');
        return [];
      }
    } catch (error) {
      console.error('❌ Error loading business config from Firebase:', error);
      return [];
    }
  }

  static renderIcon(iconName, className = 'w-5 h-5') {
    const IconComponent = ICON_MAP[iconName];
    if (IconComponent) {
      return <IconComponent className={className} />;
    }
    // Fallback icon
    return <MenuIcon className={className} />;
  }

  static saveMenuConfig(config) {
    localStorage.setItem('adminSidebarConfig', JSON.stringify(config));

    // Dispatch custom event for real-time updates
    window.dispatchEvent(new CustomEvent('sidebarConfigChanged', {
      detail: { config }
    }));

    // Also dispatch localStorage event for cross-tab synchronization
    window.dispatchEvent(new CustomEvent('localStorageChange', {
      detail: { key: 'adminSidebarConfig', value: JSON.stringify(config) }
    }));
  }

  // Specific save methods for each sidebar type
  static saveAdminConfig(config) {
    localStorage.setItem('adminSidebarConfig', JSON.stringify(config));
    window.dispatchEvent(new CustomEvent('adminSidebarConfigChanged', {
      detail: { config }
    }));
    console.log('💾 Admin sidebar config saved:', config);
  }

  static saveBusinessConfig(config) {
    localStorage.setItem('businessSidebarConfig', JSON.stringify(config));
    window.dispatchEvent(new CustomEvent('businessSidebarConfigChanged', {
      detail: { config }
    }));
    console.log('💾 Business sidebar config saved:', config);
  }

  static saveBusinessOwnerConfig(config) {
    localStorage.setItem('businessOwnerSidebarConfig', JSON.stringify(config));
    window.dispatchEvent(new CustomEvent('businessOwnerSidebarConfigChanged', {
      detail: { config }
    }));
    console.log('💾 Business owner sidebar config saved:', config);
  }

  // Listen for configuration changes
  static onConfigChange(callback) {
    const handler = (event) => {
      if (callback) callback(event.detail);
    };

    window.addEventListener('sidebarConfigChanged', handler);

    // Return cleanup function
    return () => {
      window.removeEventListener('sidebarConfigChanged', handler);
    };
  }

  // Force refresh all sidebars
  static refreshSidebars() {
    window.dispatchEvent(new CustomEvent('sidebarConfigChanged', {
      detail: {
        config: JSON.parse(localStorage.getItem('adminSidebarConfig') || '{}'),
        forceRefresh: true
      }
    }));
  }

  // Validate menu routes and map them to working routes
  static validateMenuRoutes(menuConfig) {
    if (!Array.isArray(menuConfig)) return [];

    return menuConfig.map(item => {
      if (item.type === 'single') {
        // Map the path to a working route
        const workingPath = getWorkingRoute(item.path);
        return {
          ...item,
          path: workingPath,
          originalPath: item.path // Keep original for reference
        };
      } else if (item.type === 'group' && item.children) {
        // Validate children paths
        const validatedChildren = item.children.map(child => ({
          ...child,
          path: getWorkingRoute(child.path),
          originalPath: child.path // Keep original for reference
        }));

        return {
          ...item,
          children: validatedChildren
        };
      }

      return item;
    });
  }

  // Get working route for a path (wrapper for external access)
  static getWorkingRoute(path) {
    return getWorkingRoute(path);
  }

  // Check if route is working (wrapper for external access)
  static isWorkingRoute(path) {
    return isWorkingRoute(path);
  }

  // Export default configurations
  static get DEFAULT_BUSINESS_SIDEBAR_CONFIG() {
    return DEFAULT_BUSINESS_SIDEBAR_CONFIG;
  }

  // Get sectoral sidebar config
  static getSectoralSidebarConfig(sectorType) {
    try {
      // Check for direct sectoral config by sector type
      const directSectorConfig = localStorage.getItem(`sectorSidebarConfig_${sectorType}`);
      if (directSectorConfig) {
        const config = JSON.parse(directSectorConfig);
        console.log(`🌍 Loaded sectoral config for ${sectorType}:`, config);
        return Array.isArray(config) ? config : [];
      }

      // Cross-reference fallbacks for sector compatibility
      let fallbackConfigKey = null;

      // Food & Beverage fallback
      if (sectorType === 'food_beverage' || sectorType === 'restaurant' || sectorType === 'food' || sectorType === 'restoran') {
        fallbackConfigKey = 'sectorSidebarConfig_food-beverage';
      }
      // Service fallback
      else if (sectorType === 'service' || sectorType === 'hizmet') {
        fallbackConfigKey = 'sectorSidebarConfig_service';
      }
      // Hospitality fallback
      else if (sectorType === 'hospitality' || sectorType === 'otel' || sectorType === 'turizm') {
        fallbackConfigKey = 'sectorSidebarConfig_hospitality';
      }
      // Retail fallback
      else if (sectorType === 'retail' || sectorType === 'perakende') {
        fallbackConfigKey = 'sectorSidebarConfig_retail';
      }

      if (fallbackConfigKey) {
        const fallbackConfig = localStorage.getItem(fallbackConfigKey);
        if (fallbackConfig) {
          const config = JSON.parse(fallbackConfig);
          console.log(`🌍 Using fallback config for ${sectorType} sector:`, config);
          return Array.isArray(config) ? config : [];
        }
      }

      console.log(`⚠️ No sectoral config found for ${sectorType}`);
      return [];
    } catch (error) {
      console.error(`❌ Error loading sectoral config for ${sectorType}:`, error);
      return [];
    }
  }

  // Add Firebase-based loading method for sectoral sidebar
  static async loadSectoralSidebarConfigFromFirebase(sectorType) {
    try {
      if (!db) {
        console.log('⚠️ Firebase not available for sectoral config');
        return [];
      }

      // Sector mapping for Firebase config IDs
      let configId;

      // Map incoming sectorType to Firebase config document ID
      if (sectorType === 'sectoral_food-beverage' || sectorType === 'food-beverage' || sectorType === 'food_beverage' ||
        sectorType === 'restaurant' || sectorType === 'restoran' || sectorType === 'food') {
        configId = 'sectoral_food-beverage';
      }
      else if (sectorType === 'sectoral_retail' || sectorType === 'retail' || sectorType === 'perakende') {
        configId = 'sectoral_retail';
      }
      else if (sectorType === 'sectoral_service' || sectorType === 'service' || sectorType === 'hizmet') {
        configId = 'sectoral_service';
      }
      else if (sectorType === 'sectoral_hospitality' || sectorType === 'hospitality' || sectorType === 'hotel' || sectorType === 'turizm') {
        configId = 'sectoral_hospitality';
      }
      else {
        // Default fallback
        configId = 'sectoral_food-beverage';
      }

      console.log(`🔍 MenuConfigService: Mapping sectorType "${sectorType}" to Firebase config ID "${configId}"`);

      // Try sidebarConfigs structure
      const configDoc = doc(db, 'sidebarConfigs', configId);
      const docSnap = await getDoc(configDoc);

      if (docSnap.exists()) {
        const data = docSnap.data();

        // Support both old format (menuItems) and new format (menuConfig)
        const menuConfig = data.menuConfig || data.menuItems || [];
        console.log(`🔄 Loading sectoral config for ${sectorType} (mapped to ${configId}):`, menuConfig.length || 0, 'items');
        console.log(`📊 DEBUG MenuConfigService: Data keys:`, Object.keys(data));
        console.log(`📅 DEBUG MenuConfigService: Last updated:`, data.updated_at || data.lastUpdated);

        // Save to localStorage for caching with original sectorType
        localStorage.setItem(`sectorSidebarConfig_${sectorType}`, JSON.stringify(menuConfig));

        return menuConfig;
      }

      console.log(`📝 No Firebase sectoral config found for ${sectorType} (tried ${configId})`);
      return [];
    } catch (error) {
      console.error(`❌ Error loading sectoral config from Firebase for ${sectorType}:`, error);
      return [];
    }
  }

  // Async version for sectoral sidebar
  static async getSectoralSidebarConfigAsync(sectorType) {
    try {
      // First try Firebase, then fallback to localStorage
      const firebaseConfig = await this.loadSectoralSidebarConfigFromFirebase(sectorType);

      if (firebaseConfig && firebaseConfig.length > 0) {
        return firebaseConfig;
      }

      // Fallback to localStorage
      return this.getSectoralSidebarConfig(sectorType);
    } catch (error) {
      console.error(`Error loading sectoral sidebar config async for ${sectorType}:`, error);
      return this.getSectoralSidebarConfig(sectorType);
    }
  }

  // Save sectoral config
  static saveSectoralConfig(sectorType, config) {
    localStorage.setItem(`sectorSidebarConfig_${sectorType}`, JSON.stringify(config));
    window.dispatchEvent(new CustomEvent('sectoralSidebarConfigChanged', {
      detail: { sectorType, config }
    }));
    console.log(`💾 Sectoral sidebar config saved for ${sectorType}:`, config);
  }

  // Clear all cached sidebar data
  static clearAllSidebarCache() {
    console.log('🧹 Clearing all sidebar cache...');

    // Clear all sidebar-related localStorage items
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('sectorSidebarConfig_') ||
        key === 'adminSidebarConfig' ||
        key === 'businessSidebarConfig' ||
        key === 'businessOwnerSidebarConfig' ||
        key === 'menuConfig'
      )) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      console.log(`🗑️ Removed cached config: ${key}`);
    });

    // Dispatch cache cleared event
    window.dispatchEvent(new CustomEvent('sidebarCacheCleared', {
      detail: { clearedKeys: keysToRemove, timestamp: new Date().toISOString() }
    }));

    console.log(`✅ Cleared ${keysToRemove.length} cached sidebar configurations`);
  }

  // Clear specific sector cache
  static clearSectorCache(sectorType) {
    console.log(`🧹 Clearing cache for sector: ${sectorType}`);

    const cacheKey = `sectorSidebarConfig_${sectorType}`;
    localStorage.removeItem(cacheKey);

    // Also clear related sector variants
    const variants = [
      sectorType,
      sectorType.replace('_', '-'),
      sectorType.replace('-', '_'),
      'restaurant', // restaurant is often used as fallback
      'food-beverage',
      'food_beverage'
    ];

    variants.forEach(variant => {
      const variantKey = `sectorSidebarConfig_${variant}`;
      if (localStorage.getItem(variantKey)) {
        localStorage.removeItem(variantKey);
        console.log(`🗑️ Removed variant cache: ${variantKey}`);
      }
    });

    // Dispatch sector cache cleared event
    window.dispatchEvent(new CustomEvent('sectorCacheCleared', {
      detail: { sectorType, timestamp: new Date().toISOString() }
    }));
  }

  // Initialize event listeners for admin panel changes
  static initializeEventListeners() {
    console.log('🎧 Initializing MenuConfigService event listeners...');

    // Listen for cache clearing events from admin panel
    window.addEventListener('menuConfigChanged', (event) => {
      console.log('🔄 Received menuConfigChanged event:', event.detail);

      if (event.detail?.clearCache) {
        this.clearAllSidebarCache();
      }

      if (event.detail?.sectorType) {
        this.clearSectorCache(event.detail.sectorType);
      }

      // Force reload of configurations
      this.refreshSidebars();
    });

    // Listen for sectoral sidebar updates from admin panel
    window.addEventListener('sidebarUpdated', (event) => {
      console.log('🔄 Received sidebarUpdated event:', event.detail);

      const { sectorType, config } = event.detail;

      if (sectorType && config) {
        // Update local cache with new config
        this.saveSectoralConfig(sectorType, config);

        // Clear old cache to force refresh
        this.clearSectorCache(sectorType);

        // Notify components to refresh
        window.dispatchEvent(new CustomEvent('sectoralSidebarRefresh', {
          detail: { sectorType, config, timestamp: new Date().toISOString() }
        }));
      }
    });

    // Listen for admin config changes
    window.addEventListener('adminConfigSaved', (event) => {
      console.log('🔄 Received adminConfigSaved event:', event.detail);

      // Clear admin cache
      localStorage.removeItem('adminSidebarConfig');

      // Dispatch admin refresh event
      window.dispatchEvent(new CustomEvent('adminSidebarRefresh', {
        detail: { timestamp: new Date().toISOString() }
      }));
    });

    // Listen for business config changes
    window.addEventListener('businessConfigSaved', (event) => {
      console.log('🔄 Received businessConfigSaved event:', event.detail);

      // Clear business cache
      localStorage.removeItem('businessSidebarConfig');
      localStorage.removeItem('businessOwnerSidebarConfig');

      // Dispatch business refresh event
      window.dispatchEvent(new CustomEvent('businessSidebarRefresh', {
        detail: { timestamp: new Date().toISOString() }
      }));
    });

    console.log('✅ MenuConfigService event listeners initialized');
  }

  // Force reload from Firebase (bypass cache)
  static async forceReloadFromFirebase(sectorType) {
    console.log(`🔄 Force reloading ${sectorType} config from Firebase...`);

    // Clear local cache first
    this.clearSectorCache(sectorType);

    // Load fresh from Firebase
    const freshConfig = await this.loadSectoralSidebarConfigFromFirebase(sectorType);

    if (freshConfig && freshConfig.length > 0) {
      console.log(`✅ Fresh config loaded for ${sectorType}:`, freshConfig.length, 'items');

      // Notify components of the fresh data
      window.dispatchEvent(new CustomEvent('sectoralConfigReloaded', {
        detail: { sectorType, config: freshConfig, timestamp: new Date().toISOString() }
      }));

      return freshConfig;
    } else {
      console.log(`⚠️ No fresh config found for ${sectorType}`);
      return [];
    }
  }

  // Check if cache needs refresh (based on timestamp)
  static isCacheStale(cacheKey, maxAgeMinutes = 30) {
    try {
      const cacheTimestampKey = `${cacheKey}_timestamp`;
      const timestamp = localStorage.getItem(cacheTimestampKey);

      if (!timestamp) {
        return true; // No timestamp means stale
      }

      const cacheTime = new Date(timestamp);
      const now = new Date();
      const ageMinutes = (now - cacheTime) / (1000 * 60);

      return ageMinutes > maxAgeMinutes;
    } catch (error) {
      console.error('Error checking cache staleness:', error);
      return true; // Error means treat as stale
    }
  }

  // Set cache timestamp
  static setCacheTimestamp(cacheKey) {
    const timestampKey = `${cacheKey}_timestamp`;
    localStorage.setItem(timestampKey, new Date().toISOString());
  }
}

export default MenuConfigService;